import { ArrowLeft, ArrowRight, Star } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { LlmModel } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingState } from "@/components/feedback/LoadingState";
import { useLlmModels } from "@/features/llms/hooks/useLlmModels";
import {
  useTeamAllowedModels,
  useUpdateTeamAllowedModels,
} from "@/features/teams/hooks/useTeamAllowedModels";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  teamName: string;
}

interface SelectedItem {
  llm_model_id: string;
  is_default: boolean;
}

export function AllowedModelsDialog({
  open,
  onOpenChange,
  teamId,
  teamName,
}: Props) {
  const { t } = useTranslation("teams");
  const { t: tc } = useTranslation("common");
  const { data: catalog, isLoading: catLoading } = useLlmModels({
    include_inactive: false,
  });
  const { data: current, isLoading: curLoading } =
    useTeamAllowedModels(teamId);
  const save = useUpdateTeamAllowedModels(teamId);

  const [selected, setSelected] = useState<SelectedItem[]>([]);

  useEffect(() => {
    if (open && current) {
      setSelected(
        current.map((r) => ({
          llm_model_id: r.llm_model_id,
          is_default: r.is_default,
        })),
      );
    }
  }, [open, current]);

  const selectedIds = useMemo(
    () => new Set(selected.map((s) => s.llm_model_id)),
    [selected],
  );

  // Auto-default: se ha exatamente 1 modelo selecionado e nenhum esta marcado
  // como default, marcar automaticamente. Cobre o caso "usuario adicionou 1
  // modelo e nao clicou na estrela".
  useEffect(() => {
    if (selected.length === 1 && !selected[0]!.is_default) {
      setSelected([{ ...selected[0]!, is_default: true }]);
    }
  }, [selected]);

  const available = useMemo(() => {
    if (!catalog) return [];
    return catalog.filter((m) => !selectedIds.has(m.id));
  }, [catalog, selectedIds]);

  const catalogById = useMemo(() => {
    const map = new Map<string, LlmModel>();
    catalog?.forEach((m) => map.set(m.id, m));
    return map;
  }, [catalog]);

  function addModel(modelId: string) {
    setSelected((prev) => [
      ...prev,
      // Se este e o primeiro modelo, ja marca como default direto.
      { llm_model_id: modelId, is_default: prev.length === 0 },
    ]);
  }

  function removeModel(modelId: string) {
    setSelected((prev) =>
      prev.filter((s) => s.llm_model_id !== modelId),
    );
  }

  function setAsDefault(modelId: string) {
    setSelected((prev) =>
      prev.map((s) => ({ ...s, is_default: s.llm_model_id === modelId })),
    );
  }

  async function handleSave() {
    try {
      await save.mutateAsync({ items: selected });
      toast.success(t("toasts.modelsUpdated"));
      onOpenChange(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t("allowedModelsDialog.title", { name: teamName })}
          </DialogTitle>
          <DialogDescription>
            {t("allowedModelsDialog.description")}
          </DialogDescription>
        </DialogHeader>

        {catLoading || curLoading ? (
          <LoadingState />
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">
                {t("allowedModelsDialog.available")}
              </h3>
              <ul className="max-h-72 divide-y overflow-y-auto rounded-md border">
                {available.length === 0 ? (
                  <li className="px-3 py-4 text-center text-sm text-muted-foreground">
                    {t("allowedModelsDialog.allAdded")}
                  </li>
                ) : (
                  available.map((m) => (
                    <li
                      key={m.id}
                      className="flex items-center justify-between gap-2 px-3 py-2"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {m.display_name}
                        </span>
                        <span className="text-xs font-mono text-muted-foreground">
                          {m.provider}/{m.model_id}
                        </span>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => addModel(m.id)}
                        aria-label={t("allowedModelsDialog.addAria")}
                      >
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </li>
                  ))
                )}
              </ul>
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">
                {t("allowedModelsDialog.allowed")}
              </h3>
              <ul className="max-h-72 divide-y overflow-y-auto rounded-md border">
                {selected.length === 0 ? (
                  <li className="px-3 py-4 text-center text-sm text-muted-foreground">
                    {t("allowedModelsDialog.emptySelected")}
                  </li>
                ) : (
                  selected.map((s) => {
                    const m = catalogById.get(s.llm_model_id);
                    if (!m) return null;
                    return (
                      <li
                        key={s.llm_model_id}
                        className="flex items-center justify-between gap-2 px-3 py-2"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {m.display_name}
                          </span>
                          <span className="text-xs font-mono text-muted-foreground">
                            {m.provider}/{m.model_id}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant={s.is_default ? "default" : "ghost"}
                            onClick={() => setAsDefault(s.llm_model_id)}
                            aria-label={t("allowedModelsDialog.setDefaultAria")}
                            title={
                              s.is_default
                                ? t("allowedModelsDialog.currentDefault")
                                : t("allowedModelsDialog.makeDefault")
                            }
                          >
                            <Star
                              className="h-4 w-4"
                              fill={s.is_default ? "currentColor" : "none"}
                            />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => removeModel(s.llm_model_id)}
                            aria-label={t("allowedModelsDialog.removeAria")}
                          >
                            <ArrowLeft className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {tc("actions.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={save.isPending}>
            {tc("actions.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
